import React from "react";

import { useAccessControl } from "../../../app/access-control-context";
import { useCanFetchStaffPositionHistory } from "../../../hooks/use-staff";
import { useEmploymentHistory } from "../../../hooks/use-employment-history";
import { useStaffMember } from "../../../hooks/use-user";
import { FormHelpers } from "../../forms";
import { LoadingOverlay } from "../../loading-overlay/loading-overlay";
import { SimpleTable } from "../../table";
import * as Permissions from "../../../data/permissions";

export interface ContactProps {
  staffMemberId: string;
  onEdit?: () => void;
}

export const Contact: React.FC<ContactProps> = ({ staffMemberId, onEdit }) => {
  const { activeCenterId, hasFeatureAccess, hasPermission } = useAccessControl();
  const canFetchPositionHistory = useCanFetchStaffPositionHistory(staffMemberId);
  const staffMemberQuery = useStaffMember({ centerId: activeCenterId, staffMemberId });
  const employmentHistoryQuery = useEmploymentHistory(activeCenterId, staffMemberId, {
    enabled: canFetchPositionHistory
  });
  const currentEmployment = employmentHistoryQuery.employmentHistory[0];
  const showAboutTheOwner = currentEmployment?.position === "center_owner" && !currentEmployment.end;

  const isStaffBioVisible = hasFeatureAccess?.("staff_profile") && hasPermission?.(Permissions.STAFF_VIEW_BIO);
  const staffBioRows = (
    [
      ["Graduated From", "graduatedFrom"],
      ["Degree", "degree"],
      ["Area of Study", "areaOfStudy"],
      ["Teaching Since", "teachingSince"],
      ["Certifications Earned", "certificationsEarned"],
      ["Volunteers For", "volunteersFor"],
      ["Org/Chamber Memberships", "chamberMembership"],
      ["Why HLC?", "whyHlc"],
      ["Hobbies", "hobbies"],
      ...(showAboutTheOwner ? ([["About the Owner", "aboutTheOwner"]] as const) : [])
    ] as const
  )
    .filter(([_, key]) => staffMemberQuery.data?.staffProfile[key])
    .map(([label, key]) => (
      <tr key={key} onClick={onEdit}>
        <th>{label}</th>
        <td>{staffMemberQuery.data?.staffProfile[key]}</td>
      </tr>
    ));

  return (
    <>
      <SimpleTable
        isLoading={staffMemberQuery.isLoading}
        tableCollapseScreenSize="lg"
        uniqueIdKey="id"
        headers={[
          {
            name: "Staff Name",
            renderKey: "displayName",
            render: ({ displayName }) => <a href="#">{displayName}</a>,
            tableDataCellProps: { className: "td-primary" }
          },
          {
            name: "HLC Account",
            renderKey: "businessEmail",
            render: ({ businessEmail }) => <a href={`mailto:${businessEmail}`}>{businessEmail}</a>
          },
          {
            name: "Publish Staff Info",
            renderKey: "staffProfile.isProfilePublished",
            render: ({ staffProfile: { isProfilePublished } }) =>
              isProfilePublished ? (
                <span className="fas fa-check-circle active" aria-hidden="true" />
              ) : (
                <span className="fas fa-times-circle" aria-hidden="true" />
              ),
            tableDataCellProps: { className: "td-icon" },
            tableHeaderCellProps: { className: "text-center" }
          },
          ...(hasFeatureAccess?.("employee_id")
            ? [
                {
                  name: "HR ID",
                  renderKey: "employeeNumber",
                  render: ({ employeeNumber }: { employeeNumber: string | number | null }) => employeeNumber,
                  tableDataCellProps: { className: "text-right" },
                  tableHeaderCellProps: { className: "text-right" }
                }
              ]
            : [])
        ]}
        data={staffMemberQuery.data ? [staffMemberQuery.data] : []}
        onRowClick={onEdit}
      />

      <h3 className="card-subheader">Contact</h3>
      <SimpleTable
        className="vertical-top"
        isLoading={staffMemberQuery.isLoading}
        tableCollapseScreenSize="lg"
        uniqueIdKey="id"
        headers={[
          {
            name: "Address",
            renderKey: "address",
            render: ({ line1, line2, city, state, zipcode }) => {
              const addressLines = formatAddressLines({ line1, line2, city, state, zipcode });
              return <span>{interleave(addressLines, <br key="br" />)}</span>;
            }
          },
          {
            name: "Phone Number(s)",
            renderKey: "phones",
            render: ({ mobilePhone, homePhone, otherPhone }) =>
              [
                { phone: mobilePhone, phoneType: "Mobile" },
                { phone: homePhone, phoneType: "Home" },
                { phone: otherPhone, phoneType: "Other" }
              ]
                .filter(({ phone }) => phone)
                .map(({ phone, phoneType }) => (
                  <span key={phoneType}>
                    {FormHelpers.formatPhoneNumber(phone)} ({phoneType})
                    <br />
                  </span>
                ))
          },
          {
            name: "Personal Email",
            renderKey: "personalEmail",
            render: ({ personalEmail }) => <a href={`mailto:${personalEmail}`}>{personalEmail}</a>
          },
          {
            name: "Emergency Contact",
            renderKey: "emergencyContact",
            render: ({ emergencyContactName, emergencyContactPhone }) => (
              <span>
                {emergencyContactName}
                <br />
                {FormHelpers.formatPhoneNumber(emergencyContactPhone)}
              </span>
            ),
            tableDataCellProps: { className: "text-right" },
            tableHeaderCellProps: { className: "text-right" }
          }
        ]}
        data={staffMemberQuery.data ? [staffMemberQuery.data] : []}
        onRowClick={onEdit}
      />

      {isStaffBioVisible && staffBioRows.length > 0 && (
        <>
          <h3 className="card-subheader">Staff Bio</h3>
          <LoadingOverlay loading={staffMemberQuery.isLoading}>
            <table className="table table-hover table-collapse-lg table-has-icons">
              <tbody>{staffBioRows}</tbody>
            </table>
          </LoadingOverlay>
        </>
      )}
    </>
  );
};

const interleave = <TArrayElement, TElement>(arr: TArrayElement[], el: TElement): (TArrayElement | TElement)[] =>
  arr.flatMap((x, i) => (i === arr.length - 1 ? [x] : [x, el]));

const normalizeAddressPart = (value: string | null | undefined) => value?.trim() ?? "";

const formatAddressLines = ({
  line1,
  line2,
  city,
  state,
  zipcode
}: {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string | null;
  zipcode?: string;
}) => {
  const streetLine = [line1, line2].map(normalizeAddressPart).filter(Boolean).join(" ");
  const cityState = [city, state].map(normalizeAddressPart).filter(Boolean).join(", ");
  const cityStateZipLine = [cityState, normalizeAddressPart(zipcode)].filter(Boolean).join(" ");

  return [streetLine, cityStateZipLine].filter(Boolean);
};
